/**
 * SolarZ — Reactions helpers (Phase 1: inbound detection only)
 * Localização sugerida: dashboard/helper/szReactions.js
 */

/**
 * Regex que casa com strings compostas APENAS por emojis (modifiers, ZWJ, VS16
 * e regional indicators p/ bandeiras). Exclui texto, números e pontuação.
 *
 * SZ 2026-07-09 (bug "reply numérico vira reação"): NÃO usar \p{Emoji_Component}
 * — essa propriedade Unicode INCLUI os dígitos 0-9, '#' e '*' (bases dos emojis
 * de keycap, ex. 1-keycap), então "000"/"1" casavam e um quoted-reply numérico
 * era renderizado como chip de reação. Trocado por \p{Regional_Indicator}
 * (mantém bandeiras). ZWJ/VS16/skin-tones seguem cobertos. Trade-off: keycap
 * deixa de ser detectado — não é reação padrão do WhatsApp.
 */
const EMOJI_ONLY_RE =
  /^(?:\p{Extended_Pictographic}|\p{Emoji_Modifier}|\p{Regional_Indicator}|\u200d|\ufe0f)+$/u;

/**
 * Determina se uma mensagem é uma reação (emoji-only + in_reply_to aponta para outra msg).
 *
 * O Evo Go/Api/WhatsApp Cloud já entregam reações como mensagens com
 * `content_attributes.in_reply_to = <target_message_id>` e content = emoji.
 *
 * @param {Object} msg - mensagem em camelCase
 * @returns {Boolean}
 */
export function isReactionMessage(msg) {
  if (!msg) return false;
  const inReply = msg?.contentAttributes?.inReplyTo;
  if (!inReply) return false;
  const content = (msg.content || '').trim();
  if (!content || content.length > 16) return false;
  return EMOJI_ONLY_RE.test(content);
}

/**
 * Dado um array de mensagens (camelCase), devolve:
 *   { reactionsByTarget: Map<Number, Array<ReactionEntry>>, reactionMessageIds: Set<Number> }
 *
 * ReactionEntry = { id, emoji, senderType, senderId, senderName, createdAt, messageType }
 *
 * Em caso de múltiplas reações do MESMO sender na mesma target, mantém apenas a mais recente
 * (comportamento do WhatsApp: 1 reação por usuário por mensagem, nova substitui antiga).
 */
export function buildReactionIndex(messages) {
  const reactionsByTarget = new Map();
  const reactionMessageIds = new Set();
  if (!Array.isArray(messages)) return { reactionsByTarget, reactionMessageIds };

  // Primeiro passo: tag e coleta
  const raw = new Map(); // targetId -> Map<senderKey, entry>
  for (const msg of messages) {
    if (!isReactionMessage(msg)) continue;
    reactionMessageIds.add(msg.id);
    const targetId = msg.contentAttributes.inReplyTo;
    const senderKey = `${msg.senderType || 'unknown'}:${msg.senderId ?? msg.sender?.id ?? 'null'}`;
    const entry = {
      id: msg.id,
      emoji: (msg.content || '').trim(),
      senderType: msg.senderType || msg.sender?.type || 'unknown',
      senderId: msg.senderId ?? msg.sender?.id ?? null,
      senderName: msg.sender?.name || msg.sender?.availableName || '',
      createdAt: msg.createdAt || 0,
      messageType: msg.messageType,
    };

    if (!raw.has(targetId)) raw.set(targetId, new Map());
    const senderMap = raw.get(targetId);
    const prev = senderMap.get(senderKey);
    if (!prev || entry.createdAt >= prev.createdAt) {
      senderMap.set(senderKey, entry);
    }
  }

  // Consolidar: 1 reação por sender → array
  for (const [targetId, senderMap] of raw.entries()) {
    reactionsByTarget.set(
      targetId,
      Array.from(senderMap.values()).sort((a, b) => a.createdAt - b.createdAt)
    );
  }

  return { reactionsByTarget, reactionMessageIds };
}


/**
 * Ref global que guarda o ID da mensagem cujo picker de reação está aberto.
 * Cada Message.vue consulta esse valor e exibe o picker apenas se bate com seu id.
 * Assim, abrir um picker fecha os outros automaticamente.
 */
import { ref as __szRef } from 'vue';
export const openReactionPickerId = __szRef(null);
export function openReactionPickerFor(id) {
  openReactionPickerId.value = id;
}
export function closeReactionPicker() {
  openReactionPickerId.value = null;
}
